import { Ed25519KeyIdentity } from '@dfinity/identity'
import { Secp256k1KeyIdentity } from '@dfinity/identity-secp256k1'
import { base64ToArrayBuffer } from '@plug-core/utils/arrayBuffer'
import { fromHex, toHex } from '@dfinity/agent'

const ED25519_KEY_INIT = '3053020101300506032b657004220420'
const ED25519_KEY_SEPARATOR = 'a123032100'
const ED25519_OID = '06032b6570'

const SEC256k1_KEY_INIT = '30740201010420'
const SEC256k1_KEY_SEPARATOR = 'a00706052b8104000aa144034200'
const SEC256k1_OID = '06052b8104000a'

export enum KeyPairType {
  pem256k1 = 'PEM_256k1',
  pem25519 = 'PEM_25519',
  secretKey256k1 = 'SECRET_KEY_256k1',
}

export interface ParsedIdentity {
  identity: Ed25519KeyIdentity | Secp256k1KeyIdentity
  type: KeyPairType
}

export const parseEd25519 = (pem: string) => {
  const raw = toHex(base64ToArrayBuffer(pem))

  if (!raw.substring(0, 24).includes(ED25519_OID)) {
    return undefined
  }

  const trimRaw = raw.replace(ED25519_KEY_INIT, '').replace(ED25519_KEY_SEPARATOR, '')

  try {
    const key = new Uint8Array(fromHex(trimRaw))
    const identity = Ed25519KeyIdentity.fromSecretKey(key)
    const type = KeyPairType.pem25519
    return { identity, type }
  } catch {
    return undefined
  }
}

export const parseSec256K1 = (pem: string) => {
  const raw = toHex(base64ToArrayBuffer(pem))

  if (!raw.includes(SEC256k1_OID)) {
    return undefined
  }

  const trimRaw = raw.replace(SEC256k1_KEY_INIT, '').replace(SEC256k1_KEY_SEPARATOR, '')

  try {
    const key = new Uint8Array(fromHex(trimRaw.substring(0, 64)))
    const identity = Secp256k1KeyIdentity.fromSecretKey(key)
    const type = KeyPairType.pem256k1
    return { identity, type }
  } catch {
    return undefined
  }
}

export const getIdentityFromPem = (pem: string | ArrayBuffer | null) => {
  if (!pem) throw new Error('Missing PEM')

  let data
  if (pem instanceof ArrayBuffer) {
    data = new TextDecoder().decode(pem)
  } else {
    data = pem
  }

  const trimedPem = data
    .replace(/(-{5}.*-{5})/g, '')
    .replace('\n', '')
    // Sepk256k1 keys
    .replace('BgUrgQQACg==', '')
    .trim()

  const parsedIdentity = parseEd25519(trimedPem) || parseSec256K1(trimedPem)

  if (!parsedIdentity) throw new Error('Invalid PEM')

  return parsedIdentity
}
